import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ZARA_ADVISOR_ALIAS } from '../../../../service/advisorAccess';
import {
    FLOW_SOURCE,
    closeChartView,
    getBirthChartReport,
    getBirthChartStatus,
    getChartViewOpen,
    getChartViewSurface,
    loadBirthChart,
    openBirthChartFlow
} from '../../../../store/slices/birthChartSlice';
import { getActiveChatState, requestBirthChartFollowup } from '../../../../store/slices/chatsSlice';
import { selectBirthChartEnabled } from '../../../../store/slices/featuresSlice';
import Spinner from '../../../common/Spinner';
import BirthChartFlow from '../BirthChartFlow';
import BirthChartView from '../BirthChartView';
import styles from './styles.module.scss';

/**
 * Mounts the Birth Chart surfaces that live over the whole app (HORO-10360):
 * the collect-then-gate flow and the chart view modal. Rendered once in App,
 * next to AuthModal, and inert unless the feature flag is on.
 *
 * "View Chart" on a chat card can arrive with no report in memory (a reload, a
 * chart built in another tab), so the modal loads one on demand; if there is
 * genuinely nothing to show it hands over to the flow instead of sitting empty.
 */
const BirthChartHost = () => {
    const dispatch = useDispatch();
    const enabled = useSelector(selectBirthChartEnabled);
    const viewOpen = useSelector(getChartViewOpen);
    const surface = useSelector(getChartViewSurface);
    const report = useSelector(getBirthChartReport);
    const status = useSelector(getBirthChartStatus);
    const activeChat = useSelector(getActiveChatState);

    useEffect(() => {
        if (enabled && viewOpen && !report) dispatch(loadBirthChart());
    }, [dispatch, enabled, viewOpen, report]);

    useEffect(() => {
        if (!enabled || !viewOpen || report) return;
        if (status === 'notFound' || status === 'failed') {
            dispatch(closeChartView());
            dispatch(openBirthChartFlow({ source: FLOW_SOURCE.PILL }));
        }
    }, [dispatch, enabled, viewOpen, report, status]);

    // Closing the chart is the cue for the advisor's follow-up, like closing the
    // tarot reveal. Only when this chat announced a chart; the backend dedupes, so
    // reopening and closing again asks for nothing.
    const closeView = () => {
        dispatch(closeChartView());
        const chatId = activeChat?.chatId;
        // Not in Zara's chats: no chart is announced there, and she keeps her own flow.
        const isZaraChat = activeChat?.advisor?.alias === ZARA_ADVISOR_ALIAS;
        if (chatId && !isZaraChat && activeChat.messages?.some((m) => !!m?.payload?.birthChart)) {
            Promise.resolve(dispatch(requestBirthChartFollowup(chatId))).catch(() => {});
        }
    };

    if (!enabled) return null;

    return (
        <>
            <BirthChartFlow />
            {viewOpen && report && (
                <BirthChartView
                    report={report}
                    variant="modal"
                    surface={surface}
                    onClose={closeView}
                    onCtaClose={() => dispatch(closeChartView())}
                />
            )}
            {viewOpen && !report && (
                <div className={styles.loading} aria-busy="true">
                    <Spinner width={40} height={40} />
                </div>
            )}
        </>
    );
};

export default BirthChartHost;
